import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo } from 'react'

import { useAppStore } from '@src/app/context'
import { logError } from '@src/lib/log'

import AudioDeviceCombobox from './AudioDeviceCombobox'
import type { AudioDeviceElementProps } from './AudioDeviceProps'
import VolumeIndicator from './VolumeIndicator'
import useAudioDevice from './useAudioDevice'

function MicrophoneCombobox({
  id,
  isDisabled,
  themeKey,
  showVolumeIndicator,
}: AudioDeviceElementProps) {
  const app = useAppStore()
  const devices = useMemo(
    () => [...app.service.voice.inputDevices.values()],
    [app.service.voice.inputDevices],
  )
  const audioDevice = useAudioDevice('microphone', devices)
  const handleSelectionChange = useCallback(
    (deviceId: string) => {
      app.service.voice.setAudioInputDevice(deviceId).catch(app.toast.showError)
    },
    [app.service.voice, app.toast.showError],
  )

  useEffect(() => {
    // Manually update the voice store after the persmissions are changed to
    // display the updated list of availabe devices
    app.service.voice.device?.audio?._updateAvailableDevices()?.catch(logError)
  }, [app.service.voice.device?.audio, isDisabled])

  return (
    <>
      <AudioDeviceCombobox
        audioDevice={audioDevice}
        aria-label="Microphone Devices"
        id={id}
        isDisabled={isDisabled}
        onSelectionChange={handleSelectionChange}
        themeKey={themeKey}
      />
      {showVolumeIndicator && <VolumeIndicator isDisabled={isDisabled} />}
    </>
  )
}

export default observer(MicrophoneCombobox)
